import { Alert, Badge, Button, EmptyState, Field, Input, Skeleton } from '@personachat/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, FileText, GripVertical, Pencil, Plus, Sparkles } from 'lucide-react';
import { useEffect, useId, useState, type DragEvent, type FormEvent } from 'react';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import { ErreurChargement, Select } from '../../components/bits';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { API_URL } from '../../lib/env';
import { formatDate, formatNombre } from '../../lib/format';
import { PageHeader } from '../../pages/PageHeader';

import { deplacer, initiales, useCatalogue, useEcritureContenus, type CoachCatalogue, type ModuleCatalogue } from './catalogue';

/**
 * Modules et coachs (cahier des charges §7.7) : l'arbre du catalogue dans
 * l'ordre vu par les élèves. Le super admin crée, réordonne (glisser-déposer
 * ou flèches) et règle ; l'admin consulte. Fiche coach en panneau (/coachs/:id).
 */
export function CoachsPage() {
  const catalogue = useCatalogue();
  const ecriture = useEcritureContenus();
  const toast = useToast();
  const client = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [moduleEdite, setModuleEdite] = useState<ModuleCatalogue | 'nouveau' | null>(null);
  const [nouveauCoach, setNouveauCoach] = useState<string | null>(null);
  const [ordreModules, setOrdreModules] = useState<string[] | null>(null);
  const [glisse, setGlisse] = useState<string | null>(null);

  // Lien du journal d'audit (?module=…) : la fiche du module s'ouvre.
  const moduleDemande = params.get('module');
  useEffect(() => {
    if (!moduleDemande || !catalogue.data) return;
    const m = catalogue.data.find((x) => x.id === moduleDemande);
    if (m) setModuleEdite(m);
    setParams((a) => {
      const s = new URLSearchParams(a);
      s.delete('module');
      return s;
    }, { replace: true });
  }, [moduleDemande, catalogue.data]);

  const modules = catalogue.data ?? [];
  const affiches = ordreModules ? ordreModules.map((id) => modules.find((m) => m.id === id)).filter((m): m is ModuleCatalogue => !!m) : modules;

  const enregistrerOrdre = async (ids: string[]) => {
    try {
      await lire(api.PUT('/api/admin/catalog/modules/order', { body: { ids } }));
      await client.invalidateQueries({ queryKey: ['catalogue'] });
      toast.succes('Ordre des modules enregistré.');
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setOrdreModules(null);
    }
  };

  const survolModule = (e: DragEvent, cible: string) => {
    if (!glisse || !modules.some((m) => m.id === glisse)) return;
    e.preventDefault();
    const ids = ordreModules ?? modules.map((m) => m.id);
    if (glisse === cible) return;
    setOrdreModules(deplacer(ids, glisse, ids.indexOf(cible)));
  };

  const finGlisseModule = () => {
    const ids = ordreModules;
    setGlisse(null);
    if (ids && ids.join() !== modules.map((m) => m.id).join()) void enregistrerOrdre(ids);
    else setOrdreModules(null);
  };

  return (
    <>
      <PageHeader
        title="Modules et coachs"
        lead={
          ecriture
            ? 'L’ordre ici est celui que voient les élèves. Glissez un module ou un coach pour le déplacer (ou utilisez les flèches).'
            : 'Consultation : seul un super admin modifie les modules, les coachs et leurs prompts.'
        }
        actions={
          ecriture ? (
            <>
              <Button icon={<Plus size={15} aria-hidden="true" />} onClick={() => setModuleEdite('nouveau')}>
                Nouveau module
              </Button>
              <Button variant="primary" icon={<Plus size={15} aria-hidden="true" />} onClick={() => setNouveauCoach(modules[0]?.id ?? '')} disabled={modules.length === 0}>
                Nouveau coach
              </Button>
            </>
          ) : undefined
        }
      />

      {catalogue.isError ? (
        <ErreurChargement erreur={catalogue.error} onReessayer={() => void catalogue.refetch()} />
      ) : !catalogue.data ? (
        <div className="stack">
          <Skeleton height={160} />
          <Skeleton height={160} />
        </div>
      ) : modules.length === 0 ? (
        <EmptyState icon={<Sparkles size={28} aria-hidden="true" />} title="Aucun module" text="Créez un module, puis ses coachs." />
      ) : (
        <ol className="catalogue" aria-label="Modules">
          {affiches.map((m, i) => (
            <li
              key={m.id}
              className={glisse === m.id ? 'catalogue__module catalogue__module--glisse' : 'catalogue__module'}
              onDragOver={(e) => survolModule(e, m.id)}
              onDrop={(e) => e.preventDefault()}
            >
              <div
                className="catalogue__entete"
                draggable={ecriture}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', m.id);
                  setGlisse(m.id);
                }}
                onDragEnd={finGlisseModule}
              >
                {ecriture && <GripVertical size={16} className="catalogue__poignee" aria-hidden="true" />}
                <div className="catalogue__titre">
                  <strong>{m.name}</strong>
                  <span className="muted small">
                    {formatNombre(m.coaches.length)} coach{m.coaches.length > 1 ? 's' : ''}
                    {m.category && ` · ${m.category}`}
                    {m.events.length > 0 && ` · événement : ${m.events.map((e) => e.name).join(', ')}`}
                  </span>
                </div>
                {!m.isActive && <Badge>Masqué</Badge>}
                {ecriture && (
                  <div className="catalogue__actions">
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Monter le module ${m.name}`}
                      disabled={i === 0}
                      onClick={() => void enregistrerOrdre(deplacer(modules.map((x) => x.id), m.id, i - 1))}
                    >
                      <ArrowUp size={15} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={`Descendre le module ${m.name}`}
                      disabled={i === affiches.length - 1}
                      onClick={() => void enregistrerOrdre(deplacer(modules.map((x) => x.id), m.id, i + 1))}
                    >
                      <ArrowDown size={15} aria-hidden="true" />
                    </button>
                    <Button size="sm" variant="ghost" icon={<Pencil size={13} aria-hidden="true" />} onClick={() => setModuleEdite(m)}>
                      Modifier
                    </Button>
                    <Button size="sm" variant="ghost" icon={<Plus size={13} aria-hidden="true" />} onClick={() => setNouveauCoach(m.id)}>
                      Ajouter un coach
                    </Button>
                  </div>
                )}
              </div>
              <ListeCoachs module={m} ecriture={ecriture} />
            </li>
          ))}
        </ol>
      )}

      {moduleEdite && <ModuleForm module={moduleEdite === 'nouveau' ? null : moduleEdite} onClose={() => setModuleEdite(null)} />}
      {nouveauCoach !== null && <NouveauCoach modules={modules} moduleId={nouveauCoach} onClose={() => setNouveauCoach(null)} />}
      <Outlet />
    </>
  );
}

/** Coachs d'un module, réordonnables (glisser-déposer et flèches) par le super admin. */
function ListeCoachs({ module, ecriture }: { module: ModuleCatalogue; ecriture: boolean }) {
  const navigate = useNavigate();
  const { id: ouvert } = useParams();
  const toast = useToast();
  const client = useQueryClient();
  const [ordre, setOrdre] = useState<string[] | null>(null);
  const [glisse, setGlisse] = useState<string | null>(null);

  const coachs = ordre
    ? ordre.map((id) => module.coaches.find((c) => c.id === id)).filter((c): c is CoachCatalogue => !!c)
    : module.coaches;
  const idsServeur = module.coaches.map((c) => c.id);

  const enregistrer = async (ids: string[]) => {
    setOrdre(ids);
    try {
      await lire(api.PUT('/api/admin/catalog/modules/{id}/coaches/order', { params: { path: { id: module.id } }, body: { ids } }));
      await client.invalidateQueries({ queryKey: ['catalogue'] });
      toast.succes('Ordre des coachs enregistré.');
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setOrdre(null);
    }
  };

  if (module.coaches.length === 0) {
    return <p className="catalogue__vide">Aucun coach dans ce module.</p>;
  }

  return (
    <ol className="catalogue__coachs" aria-label={`Coachs de ${module.name}`}>
      {coachs.map((c, i) => (
        <li
          key={c.id}
          className={['catalogue__coach', glisse === c.id ? 'catalogue__coach--glisse' : '', ouvert === c.id ? 'catalogue__coach--actif' : ''].join(' ')}
          draggable={ecriture}
          onDragStart={(e) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', c.id);
            setGlisse(c.id);
          }}
          onDragOver={(e) => {
            if (!glisse || !idsServeur.includes(glisse)) return;
            e.preventDefault();
            e.stopPropagation();
            const ids = ordre ?? idsServeur;
            if (glisse !== c.id) setOrdre(deplacer(ids, glisse, ids.indexOf(c.id)));
          }}
          onDragEnd={(e) => {
            e.stopPropagation();
            setGlisse(null);
            if (ordre && ordre.join() !== idsServeur.join()) void enregistrer(ordre);
            else setOrdre(null);
          }}
        >
          {ecriture && <GripVertical size={15} className="catalogue__poignee" aria-hidden="true" />}
          <Avatar nom={c.name} url={c.avatar} />
          <button type="button" className="catalogue__coach-lien" onClick={() => navigate(`/coachs/${c.id}`)}>
            <span className="cell-main">{c.name}</span>
            <span className="cell-sub">{c.description || 'Sans description'}</span>
          </button>
          <div className="catalogue__infos">
            {!c.isActive && <Badge>Inactif</Badge>}
            <span className="muted small" title="Conversations des élèves avec ce coach">
              {formatNombre(c.conversations)} conv.
            </span>
            <Link className="link small" to={`/prompts/${c.id}`} title={`Prompt : ${formatNombre(c.promptLength)} caractères, ${c.promptVersions} version(s)`}>
              <FileText size={13} aria-hidden="true" /> Prompt
              {c.promptUpdatedAt && <span className="muted"> · {formatDate(c.promptUpdatedAt)}</span>}
            </Link>
          </div>
          {ecriture && (
            <div className="catalogue__actions">
              <button
                type="button"
                className="icon-btn"
                aria-label={`Monter ${c.name}`}
                disabled={i === 0}
                onClick={() => void enregistrer(deplacer(idsServeur, c.id, i - 1))}
              >
                <ArrowUp size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label={`Descendre ${c.name}`}
                disabled={i === coachs.length - 1}
                onClick={() => void enregistrer(deplacer(idsServeur, c.id, i + 1))}
              >
                <ArrowDown size={14} aria-hidden="true" />
              </button>
            </div>
          )}
        </li>
      ))}
    </ol>
  );
}

export function Avatar({ nom, url, taille = 32 }: { nom: string; url: string | null; taille?: number }) {
  const [casse, setCasse] = useState<string | null>(null);
  // Sans stockage objet (développement), l'API sert l'image elle-même : chemin relatif à son adresse.
  const src = url?.startsWith('/') ? `${API_URL}${url}` : url;
  if (src && casse !== src) {
    return <img className="avatar-coach" src={src} alt="" width={taille} height={taille} onError={() => setCasse(src)} />;
  }
  return (
    <span className="avatar-coach avatar-coach--initiales" style={{ width: taille, height: taille }} aria-hidden="true">
      {initiales(nom)}
    </span>
  );
}

/** Création ou réglage d'un module ; suppression d'un module vide. */
function ModuleForm({ module, onClose }: { module: ModuleCatalogue | null; onClose: () => void }) {
  const id = useId();
  const toast = useToast();
  const client = useQueryClient();
  const [nom, setNom] = useState(module?.name ?? '');
  const [description, setDescription] = useState(module?.description ?? '');
  const [categorie, setCategorie] = useState(module?.category ?? '');
  const [actif, setActif] = useState(module?.isActive ?? true);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [supprimer, setSupprimer] = useState(false);
  const ecriture = useEcritureContenus();

  const valide = nom.trim().length >= 2;

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide || envoi) return;
    setEnvoi(true);
    setErreur(null);
    const body = { name: nom.trim(), description: description.trim(), category: categorie.trim(), isActive: actif };
    try {
      if (module) await lire(api.PATCH('/api/admin/catalog/modules/{id}', { params: { path: { id: module.id } }, body }));
      else await lire(api.POST('/api/admin/catalog/modules', { body }));
      toast.succes(module ? 'Module modifié.' : 'Module créé.');
      void client.invalidateQueries({ queryKey: ['catalogue'] });
      onClose();
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  const vide = module && module.coaches.length === 0 && module.events.length === 0;

  return (
    <Modal open onOpenChange={(o) => !o && !envoi && onClose()} title={module ? `Module « ${module.name} »` : 'Nouveau module'}>
      <form className="stack" onSubmit={soumettre}>
        {erreur && <Alert>{erreur}</Alert>}
        <fieldset className="fieldset-nu stack" disabled={!ecriture}>
          <Field id={`${id}-nom`} label="Nom">
            <Input id={`${id}-nom`} value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80} autoFocus />
          </Field>
          <Field id={`${id}-desc`} label="Description" hint="Affichée aux élèves sous le nom du module.">
            <textarea id={`${id}-desc`} className="ui-input textarea textarea--texte" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
          </Field>
          <Field id={`${id}-cat`} label="Catégorie (facultatif)">
            <Input id={`${id}-cat`} value={categorie} onChange={(e) => setCategorie(e.target.value)} maxLength={40} />
          </Field>
          <label className="case">
            <input type="checkbox" checked={actif} onChange={(e) => setActif(e.target.checked)} />
            Visible par les élèves
          </label>
        </fieldset>
        {module && ecriture && (
          <p className="note" style={{ margin: 0 }}>
            {vide ? (
              <button type="button" className="lien-discret" style={{ padding: 0 }} onClick={() => setSupprimer(true)}>
                Supprimer ce module
              </button>
            ) : (
              'Un module qui contient des coachs ou un événement ne peut pas être supprimé : masquez-le plutôt.'
            )}
          </p>
        )}
        <div className="modal__footer modal__footer--inline">
          <Button onClick={onClose} disabled={envoi}>
            {ecriture ? 'Annuler' : 'Fermer'}
          </Button>
          {ecriture && (
            <Button type="submit" variant="primary" loading={envoi} disabled={!valide}>
              {module ? 'Enregistrer' : 'Créer le module'}
            </Button>
          )}
        </div>
      </form>
      {module && (
        <ConfirmDialog
          open={supprimer}
          onOpenChange={setSupprimer}
          title="Supprimer le module"
          confirmLabel="Supprimer"
          tone="danger"
          onConfirm={async () => {
            await lire(api.DELETE('/api/admin/catalog/modules/{id}', { params: { path: { id: module.id } } }));
            toast.succes('Module supprimé.');
            void client.invalidateQueries({ queryKey: ['catalogue'] });
            onClose();
          }}
        >
          <p>Le module « {module.name} » est vide : il disparaît du catalogue.</p>
        </ConfirmDialog>
      )}
    </Modal>
  );
}

/** Nouveau coach : inactif tant que son prompt n'a pas été testé et qu'il n'est pas activé. */
function NouveauCoach({ modules, moduleId, onClose }: { modules: ModuleCatalogue[]; moduleId: string; onClose: () => void }) {
  const id = useId();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const [nom, setNom] = useState('');
  const [module, setModule] = useState(moduleId);
  const [description, setDescription] = useState('');
  const [prompt, setPrompt] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const valide = nom.trim().length >= 2 && !!module && prompt.trim().length >= 10;

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    if (!valide || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await lire(
        api.POST('/api/admin/catalog/coaches', { body: { name: nom.trim(), moduleId: module, description: description.trim(), systemPrompt: prompt } })
      );
      toast.succes('Coach créé, inactif : testez son prompt puis activez-le.');
      void client.invalidateQueries({ queryKey: ['catalogue'] });
      onClose();
      navigate(`/coachs/${r.coach.id}`);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Modal open onOpenChange={(o) => !o && !envoi && onClose()} title="Nouveau coach" width={640}>
      <form className="stack" onSubmit={soumettre}>
        <p className="texte-aide">Le coach est créé inactif : les élèves ne le voient qu’une fois activé dans sa fiche.</p>
        {erreur && <Alert>{erreur}</Alert>}
        <div className="grille-2">
          <Field id={`${id}-nom`} label="Nom">
            <Input id={`${id}-nom`} value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80} autoFocus placeholder="Ex. : Le Coach Ikigai" />
          </Field>
          <Field id={`${id}-module`} label="Module">
            <Select id={`${id}-module`} value={module} onChange={(e) => setModule(e.target.value)}>
              {modules.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field id={`${id}-desc`} label="Description" hint="Une phrase, affichée aux élèves.">
          <Input id={`${id}-desc`} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={1000} />
        </Field>
        <Field id={`${id}-prompt`} label="Prompt de départ" hint="10 caractères au moins. Vous l’affinerez dans l’éditeur de prompt.">
          <textarea id={`${id}-prompt`} className="ui-input textarea textarea--prompt" rows={8} value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        </Field>
        <div className="modal__footer modal__footer--inline">
          <Button onClick={onClose} disabled={envoi}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" loading={envoi} disabled={!valide}>
            Créer le coach
          </Button>
        </div>
      </form>
    </Modal>
  );
}
